/**
 * obliqueRotation.js — косые реформаты: углы → ориентация плоскости среза.
 *
 * ПОВОД. В программе три ползунка «Угол X / Y / Z», и до 10.19.11 их
 * единственный обработчик обновлял ТЕКСТОВУЮ ПОДПИСЬ рядом с ползунком
 * (`sidebarBindings.js`: пара «ползунок → значение» с форматом `v + '°'`).
 * Камера при этом не поворачивалась ни на градус, а кнопка сброса просто
 * обнуляла ползунки. То есть косых реформатов в программе не было вовсе, хотя
 * интерфейс обещал их тремя ползунками. Есть у всех: Weasis, RadiAnt,
 * MicroDicom, 3D Slicer, OHIF.
 *
 * ЧТО ТАКОЕ КОСОЙ РЕФОРМАТ. Обычные реформаты режут объём по трём плоскостям
 * пациента. Косой — та же плоскость, но повёрнутая: нужна, когда структура
 * лежит не по осям (аорта, зубной ряд, межпозвонковый диск, длинная ось ЛЖ).
 * Плоскость задаётся двумя векторами: нормалью (куда смотрим) и «верхом»
 * (viewUp, где верх кадра). Наклонить реформат — значит повернуть эту пару.
 *
 * ПОЧЕМУ ЭТО ОТДЕЛЬНЫЙ ФАЙЛ БЕЗ ИМПОРТОВ. Поворот — чистая математика, и её
 * можно проверить аналитически: поворот на 0° ничего не меняет, на 90° вокруг
 * известной оси даёт известный ответ, на 360° возвращает исходное, а нормаль и
 * верх обязаны остаться единичными и перпендикулярными. Ошибка здесь означает
 * срез не там, где врач думает, — то есть неверную анатомию на экране, а такое
 * нельзя проверять разглядыванием.
 *
 * СОГЛАШЕНИЯ, и они важны.
 *
 *   · Оси — пациентские (LPS, как в DICOM): X = лево-право, Y = перед-зад,
 *     Z = голова-ноги. Те же, в которых заданы стандартные ориентации в
 *     `core/viewport.js`.
 *   · Углы применяются В ПОРЯДКЕ X → Y → Z, вокруг НЕПОДВИЖНЫХ осей пациента
 *     (внешние повороты). Порядок зафиксирован тестом: при некоммутативности
 *     поворотов «какой сначала» — это часть ответа, а не деталь реализации.
 *   · Положительный угол — поворот против часовой стрелки, если смотреть
 *     навстречу оси (правило правой руки).
 *   · Поворачивается ПАРА векторов исходной ориентации окна, а не абсолютная
 *     система. Поэтому «Угол X = 15°» для аксиального и для сагиттального окна
 *     наклоняет по-разному — и это правильно: наклон отсчитывается от того, что
 *     врач видит.
 */

const К_РАДИАНАМ = Math.PI / 180;

/** Единичный вектор. Нулевой не нормируем — вернём как есть, решает вызывающий. */
export function единичный(v) {
  const д = Math.hypot(v[0], v[1], v[2]);
  return д > 1e-12 ? [v[0] / д, v[1] / д, v[2] / д] : [v[0], v[1], v[2]];
}

export function скалярно(a, b) { return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]; }

export function векторно(a, b) {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

/**
 * Матрица поворота вокруг оси X пациента (лево-право) на угол в градусах.
 * Возвращается как массив из трёх строк.
 */
export function вокругX(град) {
  const a = град * К_РАДИАНАМ, c = Math.cos(a), s = Math.sin(a);
  return [[1, 0, 0], [0, c, -s], [0, s, c]];
}

export function вокругY(град) {
  const a = град * К_РАДИАНАМ, c = Math.cos(a), s = Math.sin(a);
  return [[c, 0, s], [0, 1, 0], [-s, 0, c]];
}

export function вокругZ(град) {
  const a = град * К_РАДИАНАМ, c = Math.cos(a), s = Math.sin(a);
  return [[c, -s, 0], [s, c, 0], [0, 0, 1]];
}

/** Произведение матриц 3×3: сначала применяется b, потом a. */
export function умножить(a, b) {
  const о = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      о[i][j] = a[i][0] * b[0][j] + a[i][1] * b[1][j] + a[i][2] * b[2][j];
    }
  }
  return о;
}

/** Применить матрицу к вектору. */
export function применить(m, v) {
  return [
    m[0][0] * v[0] + m[0][1] * v[1] + m[0][2] * v[2],
    m[1][0] * v[0] + m[1][1] * v[1] + m[1][2] * v[2],
    m[2][0] * v[0] + m[2][1] * v[1] + m[2][2] * v[2],
  ];
}

/**
 * Общая матрица наклона по трём углам.
 *
 * Порядок X → Y → Z вокруг неподвижных осей означает: сперва вектор
 * поворачивается вокруг X, полученное — вокруг Y, полученное — вокруг Z.
 * В матричной записи это Rz·Ry·Rx (справа применяется первым).
 */
export function матрицаНаклона(углы = {}) {
  const x = Number(углы.x) || 0;
  const y = Number(углы.y) || 0;
  const z = Number(углы.z) || 0;
  return умножить(умножить(вокругZ(z), вокругY(y)), вокругX(x));
}

/**
 * Наклонить ориентацию окна.
 *
 * @param {{viewPlaneNormal: number[], viewUp: number[]}} база исходная ориентация
 * @param {{x?: number, y?: number, z?: number}} углы в градусах
 * @returns {{viewPlaneNormal: number[], viewUp: number[]}} новая ориентация,
 *          нормаль и верх единичные и строго перпендикулярные
 */
export function наклонить(база, углы = {}) {
  const m = матрицаНаклона(углы);
  let n = единичный(применить(m, база.viewPlaneNormal));
  let up = единичный(применить(m, база.viewUp));

  // Ортогонализация Грама-Шмидта. Нужна не «для красоты»: у самой матрицы
  // поворота ошибки нет, но исходная пара из чужого кода может быть слегка
  // неперпендикулярной, а Cornerstone от такой пары даёт скошенный срез —
  // и это выглядит как ошибка наклона, хотя пришло из данных.
  const проекция = скалярно(up, n);
  up = единичный([
    up[0] - проекция * n[0],
    up[1] - проекция * n[1],
    up[2] - проекция * n[2],
  ]);

  // Верх стал вырожденным (совпал с нормалью) — выбираем любой перпендикуляр,
  // иначе плоскость не определена. Молча отдать вырожденную пару нельзя.
  if (Math.hypot(up[0], up[1], up[2]) < 1e-6) {
    const запас = Math.abs(n[2]) < 0.9 ? [0, 0, 1] : [1, 0, 0];
    up = единичный(векторно(n, запас));
  }
  return { viewPlaneNormal: n, viewUp: up };
}

/** Есть ли наклон вообще — чтобы не трогать камеру зря. */
export function естьНаклон(углы = {}) {
  return (Number(углы.x) || 0) !== 0 || (Number(углы.y) || 0) !== 0 || (Number(углы.z) || 0) !== 0;
}

/**
 * Подпись для человека: угол между исходной и наклонённой нормалью.
 * Именно это число врач считает «наклоном», а не три отдельных угла.
 */
export function уголОтклонения(база, углы = {}) {
  const { viewPlaneNormal } = наклонить(база, углы);
  const c = Math.max(-1, Math.min(1, скалярно(единичный(база.viewPlaneNormal), viewPlaneNormal)));
  return Math.acos(c) / К_РАДИАНАМ;
}

export default {
  единичный, скалярно, векторно, вокругX, вокругY, вокругZ, умножить, применить,
  матрицаНаклона, наклонить, естьНаклон, уголОтклонения,
};
